import { useHistoryStore } from '../history.store'
import { patchSession } from '@/features/session/domain/use-cases/patch-session'
import useTracksStore, { type TracksStore } from '../tracks.store'
import { useAppStore } from '../app.store.ts'
import { createSession } from '@/features/session/domain/use-cases/create-session'

const upsertSession = async (): Promise<void> => {
  const { session } = useAppStore.getState()
  if (!session) {
    const session = await createSession()
    useAppStore.setState({ session })
  }
  await patchSession()
}

/**
 * Core save function that performs the actual save operation.
 * This function throws errors that TanStack Query will handle with retries.
 */
export const performSave = async (): Promise<void> => {
  const { saveStatus } = useHistoryStore.getState()

  // Don't save if already broken
  if (saveStatus === 'broken') {
    return
  }

  // Only save if there are unsaved changes (dirty) or if a previous save failed
  if (saveStatus !== 'dirty' && saveStatus !== 'failed') {
    return
  }

  // Check if any clips are missing src URLs (still being generated/fetched)
  // This includes all clip types: voices (speeches), sound designs, media files, sound effects
  const { tracks } = useTracksStore.getState()
  const clipsWithoutUrls = tracks
    .flatMap((track) => track.clips)
    .filter((clip) => !clip.src)

  // Don't save if there are clips still being generated or URLs being fetched
  if (clipsWithoutUrls.length > 0) {
    return
  }

  // Set status to syncing before save
  useHistoryStore.setState({ saveStatus: 'syncing' })
  try {
    await upsertSession()
  } catch (error) {
    useHistoryStore.setState({ saveStatus: 'failed' })
    throw error instanceof Error ? error : new Error(String(error))
  }

  useHistoryStore.setState({ saveStatus: 'persisted' })
}

const hasSaved = (state: TracksStore): boolean =>
  state.tracks.every((track) => track.clips.every((clip) => clip.src))

export const forceSave = async (): Promise<void> => {
  useHistoryStore.setState({ saveStatus: 'syncing' })
  await new Promise<void>((resolve) => {
    // Check immediately in case state is already correct
    // (subscribe only fires on changes, not current state)
    if (hasSaved(useTracksStore.getState())) {
      resolve(undefined)
      return
    }

    // Set up subscription for future state changes
    const unsubscribe = useTracksStore.subscribe((state) => {
      if (hasSaved(state)) {
        unsubscribe()
        resolve(undefined)
      }
    })

    // Safety timeout to prevent infinite blocking during logout
    // (memory leaks don't matter since user is leaving)
    setTimeout(() => {
      unsubscribe()
      resolve(undefined)
    }, 30_000) // 30 seconds max wait
  })
  await performSave()
}

/**
 * Schedules auto-save with debouncing and uses TanStack Query's retry mechanism.
 * The mutation handles retries automatically - after max retries, the onError callback marks as broken.
 * This generator continuously loops, calling saveMutation which internally decides whether to save.
 */
async function* scheduleSave(
  delay: number,
  saveMutation: () => Promise<void>,
  isCancelled: () => boolean
): AsyncGenerator<boolean> {
  // Wait for the delay to batch changes together
  await new Promise((resolve) => setTimeout(resolve, delay))

  // Check if cancelled after delay
  if (isCancelled()) {
    return false
  }

  // Call saveMutation - it internally checks if save is needed
  await saveMutation()
  yield true
  yield* scheduleSave(delay, saveMutation, isCancelled)
}

export type Unsubscribe = () => void

/**
 * Auto-save function that uses TanStack Query's retry mechanism.
 * The saveMutation should be a useMutation hook that handles retries.
 *
 * @param saveMutation - Mutation function from useMutation hook
 * @param delay - Debounce delay in milliseconds (default: 10 seconds)
 * @returns Unsubscribe function to stop auto-save
 */
export const autoSave = (
  saveMutation: () => Promise<void>,
  delay = 10_000
): Unsubscribe => {
  let cancelled = false

  const schedule = async (): Promise<void> => {
    for await (const result of scheduleSave(
      delay,
      saveMutation,
      () => cancelled
    )) {
      if (!result) break
    }
  }
  void schedule()

  return () => {
    cancelled = true
  }
}

/**
 * Creates a save mutation function that can be used with auto-save.
 * This is a simple wrapper that passes through to the mutation's mutateAsync.
 * TanStack Query handles all retries automatically.
 *
 * @param mutateAsync - The mutateAsync function from a useMutation hook
 * @returns A function that performs the save operation with retry handling
 */
export const createSaveMutation = (
  mutateAsync: () => Promise<void>
): (() => Promise<void>) => mutateAsync
